import { Trans } from 'react-i18next';
import i18n from 'locales/i18n';
import { LineChartProps } from '@components/report/chart/line-chart/LineChart';

import { TriangleChartProps } from '@components/report/chart/triangle-chart/TriangleChart';

import { YTReportRes } from '@apis/orval/model';

import round, { roundFloat } from '@utils/round';

import { FollowerGrowthYtProps } from './FollowerGrowth';

export class FollowerTrendAdapter {
	value: YTReportRes;
	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	get followerList() {
		return (
			(this.value?.follow_stats?.trend?.follower_list ?? []).map((v) => Number(v) ?? 0) ??
			[]
		);
	}
	get dateList() {
		return (this.value?.follow_stats?.trend?.date_list ?? []).map((v) => v ?? '') ?? [];
	}
	get difference() {
		return this.followerList[0] - this.followerList[this.followerList.length - 1];
	}

	get isPositive() {
		return this.difference < 0 ? 'high' : this.difference > 0 ? 'low' : 'normal';
	}

	textMapper = {
		high: i18n?.t('infReportYoutube:follower-growth.view-trend.text.high'), //' 성장했습니다.',
		low: i18n?.t('infReportYoutube:follower-growth.view-trend.text.low'), //' 하락했습니다.',
		normal: i18n?.t('infReportYoutube:follower-growth.view-trend.text.normal'), //'변동이 없습니다.',
	};

	adapt(): LineChartProps {
		if (this.followerList.length === 0) return { isError: true };
		return {
			desc: (
				<>
					{/* 구독자가{' '}
					<span>
						{this.difference !== 0 ? `${round(Math.abs(this.difference), 'down')} 명` : ''}
						{this.textMapper[this.isPositive]}
					</span> */}
					<Trans
						// t={i18n?.t}
						i18nKey={'infReportYoutube:follower-growth.follower-list.description'}
						components={[
							<span key={'difference'}>
								{this.difference !== 0
									? i18n?.t('unit.number-of-person', {
											number: round(Math.abs(this.difference), 'down'),
									  })
									: ''}
								{this.textMapper[this.isPositive]}
							</span>,
						]}
					/>
				</>
			),
			datas_a: this.followerList,
			categorys: this.dateList,
			isPositive: this.isPositive === 'high',
		};
	}
}

export class FollowerGrowthRateAdapter {
	value: YTReportRes;
	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	get followerGrowthRate() {
		return this.value?.follow_stats?.trend?.follower_growth_rate ?? 0;
	}
	get followerGrowthRateInSimilarGroup() {
		return this.value?.follow_stats?.follower_growth_ranking_rate_in_similar_group ?? 0;
	}

	adapt(): TriangleChartProps {
		return {
			desc: (
				// <>
				// 	구독자 성장률이 <span>{roundFloat(this.followerGrowthRate * 100, 'down', 2)}%</span>로<br />
				// 	유사 그룹 중{' '}
				// 	<span>
				// 		상위{' '}
				// 		{this.followerGrowthRateInSimilarGroup
				// 			? roundFloat(this.followerGrowthRateInSimilarGroup * 100, 'down', 2)
				// 			: '-'}
				// 		%
				// 	</span>
				// 	에 해당됩니다.
				// </>
				<Trans
					// t={i18n?.t}
					i18nKey={'infReportYoutube:follower-growth.follower-growth-rate.description'}
					components={[
						<span key={'follower-growth-rate'}>
							{roundFloat(this.followerGrowthRate * 100, 'down', 2)}%
						</span>,
						<span key={'percentage'}>
							{i18n?.t(
								'infReportYoutube:follower-growth.follower-growth-rate.top-similar-group',
								{
									number:
										(this.followerGrowthRateInSimilarGroup
											? roundFloat(this.followerGrowthRateInSimilarGroup, 'down', 2)
											: '-') + '%',
								},
							)}
						</span>,
					]}
				/>
			),
			valuePercentage: this.followerGrowthRateInSimilarGroup ?? undefined,
		};
	}
}

export class FollowingTrendAdapter {
	value: YTReportRes;
	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	get viewList() {
		return (this.value?.follow_stats?.trend?.view_list ?? []).map((v) => Number(v) ?? 0);
	}
	get dateList() {
		return (this.value?.follow_stats?.trend?.date_list ?? []).map((v) => v ?? '');
	}
	get difference() {
		return this.viewList[0] - this.viewList[this.viewList.length - 1];
	}

	get isPositive() {
		return this.difference < 0 ? 'high' : this.difference > 0 ? 'low' : 'normal';
	}

	textMapper = {
		high: i18n?.t('infReportYoutube:follower-growth.view-trend.text.high'), //' 성장했습니다.',
		low: i18n?.t('infReportYoutube:follower-growth.view-trend.text.low'), //' 하락했습니다.',
		normal: i18n?.t('infReportYoutube:follower-growth.view-trend.text.normal'), //'변동이 없습니다.',
	};

	adapt(): LineChartProps {
		if (this.viewList.length === 0) return { isError: true };
		return {
			desc: (
				<>
					{/* 조회 수가{' '}
					<span>
						{this.difference !== 0 ? `${round(Math.abs(this.difference), 'down')} 회` : ''}
						{this.textMapper[this.isPositive]}
					</span> */}
					<Trans
						// t={i18n?.t}
						i18nKey={'infReportYoutube:follower-growth.view-list.description'}
						components={[
							<span key={'difference'}>
								{this.difference !== 0
									? i18n?.t('unit.times', {
											number: round(Math.abs(this.difference), 'down'),
									  })
									: ''}
								{this.textMapper[this.isPositive]}
							</span>,
						]}
					/>
				</>
			),
			datas_a: this.viewList,
			categorys: this.dateList,
			isPositive: this.isPositive === 'high',
		};
	}
}

export class FollowerGrowthYtAdapter {
	value: YTReportRes;
	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	adapt(): FollowerGrowthYtProps {
		return {
			followerTrend: new FollowerTrendAdapter(this.value).adapt(),
			followerGrowthRate: new FollowerGrowthRateAdapter(this.value).adapt(),
			followingTrend: new FollowingTrendAdapter(this.value).adapt(),
		};
	}
}
